import React, { createContext, useContext, useRef, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const NavigationHistoryContext = createContext(null);

/**
 * Custom navigation history that avoids browser back/forward (and the
 * API-refetch race conditions it causes).  Maintains an in-memory stack
 * of visited paths with saved scroll positions.
 *
 * - Every path change is recorded.
 * - goBack / goForward move a cursor through that stack and navigate
 *   via `navigate(path, { replace: true, state: { _navRestore: true } })`
 *   so React Router treats it as a simple route swap (no POP event).
 * - Scroll positions are saved per stack index and restored after the
 *   page content has rendered.
 */
export function NavigationHistoryProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();

  // history = ['/dashboard', '/orders', '/quotes', ...]
  // cursor  = current index in that array
  const history = useRef([location.pathname]);
  const cursor = useRef(0);
  const scrollMap = useRef({});        // index → scrollY
  const isInternalNav = useRef(false); // true when we triggered the nav

  // Track every route change
  useEffect(() => {
    const path = location.pathname;

    if (isInternalNav.current) {
      // We caused this navigation (goBack / goForward) — don't push
      isInternalNav.current = false;

      // Restore scroll after content renders
      const idx = cursor.current;
      const saved = scrollMap.current[idx] || 0;
      if (saved > 0) {
        // Use rAF + setTimeout to wait for lazy-loaded page to paint
        requestAnimationFrame(() => {
          setTimeout(() => {
            window.scrollTo(0, saved);
            // Retry once more after another frame in case content is still loading
            requestAnimationFrame(() => window.scrollTo(0, saved));
          }, 50);
        });
      } else {
        window.scrollTo(0, 0);
      }
      return;
    }

    // Normal navigation (user clicked a tab) — save current scroll, push new entry
    scrollMap.current[cursor.current] = window.scrollY;

    const cur = cursor.current;
    // If we navigated back and then clicked a new tab, truncate forward history
    history.current = history.current.slice(0, cur + 1);
    // Don't push duplicates
    if (history.current[cur] !== path) {
      history.current.push(path);
      cursor.current = history.current.length - 1;
    }

    window.scrollTo(0, 0);
  }, [location.pathname]);

  const canGoBack = useCallback(() => cursor.current > 0, []);
  const canGoForward = useCallback(
    () => cursor.current < history.current.length - 1,
    [],
  );

  const goBack = useCallback(() => {
    if (cursor.current <= 0) return;
    // Save current scroll before leaving
    scrollMap.current[cursor.current] = window.scrollY;
    cursor.current -= 1;
    isInternalNav.current = true;
    navigate(history.current[cursor.current], {
      replace: true,
      state: { _navRestore: true },
    });
  }, [navigate]);

  const goForward = useCallback(() => {
    if (cursor.current >= history.current.length - 1) return;
    scrollMap.current[cursor.current] = window.scrollY;
    cursor.current += 1;
    isInternalNav.current = true;
    navigate(history.current[cursor.current], {
      replace: true,
      state: { _navRestore: true },
    });
  }, [navigate]);

  return (
    <NavigationHistoryContext.Provider
      value={{ goBack, goForward, canGoBack, canGoForward }}
    >
      {children}
    </NavigationHistoryContext.Provider>
  );
}

export function useNavigationHistory() {
  const ctx = useContext(NavigationHistoryContext);
  if (!ctx) throw new Error('useNavigationHistory must be inside NavigationHistoryProvider');
  return ctx;
}
